import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import styled from "styled-components";
import { theme } from "styles/theme";

const ModalContent = styled.div`
  padding: 1rem;
  min-width: 400px;
  color: ${theme.colors.textPrimary};
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: ${theme.colors.textPrimary};
`;

const Input = styled(InputText)`
  width: 100%;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const ConfirmationText = styled.p`
  margin-bottom: 1rem;
  color: ${theme.colors.textSecondary};
`;

const HintText = styled.small`
  color: ${theme.colors.textSecondary};
  font-size: 0.8rem;
`;

interface EditAliasModalProps {
  visible: boolean;
  linkId: number;
  currentAlias: string;
  serialNumber: string;
  onHide: () => void;
  onAliasUpdated: (newAlias: string) => void;
  isUpdating?: boolean;
}

export const EditAliasModal: React.FC<EditAliasModalProps> = ({
  visible,
  linkId,
  currentAlias,
  serialNumber,
  onHide,
  onAliasUpdated,
  isUpdating = false,
}) => {
  const [newAlias, setNewAlias] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (visible) {
      setNewAlias(currentAlias || "");
      setShowConfirmation(false);
    }
  }, [visible, currentAlias]);

  const handleSave = () => {
    if (newAlias.trim() === currentAlias.trim()) {
      onHide();
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmSave = () => {
    onAliasUpdated(newAlias.trim());
  };

  const handleCancel = () => {
    if (showConfirmation) {
      setShowConfirmation(false);
    } else {
      onHide();
    }
  };

  const isAliasValid =
    newAlias.trim().length > 0 && newAlias.trim().length <= 30;

  return (
    <Dialog
      header={showConfirmation ? "Confirm Alias Change" : "Edit Device Alias"}
      visible={visible}
      onHide={onHide}
      modal
      draggable={false}
      resizable={false}
      closable={!isUpdating}
    >
      <ModalContent>
        {!showConfirmation ? (
          <>
            <FormGroup>
              <Label htmlFor="device-alias">Device Alias</Label>
              <Input
                id="device-alias"
                value={newAlias}
                onChange={(e) => setNewAlias(e.target.value)}
                placeholder="Enter device alias"
                maxLength={30}
                disabled={isUpdating}
              />
              <HintText>Maximum 30 characters</HintText>
            </FormGroup>

            <ButtonGroup>
              <Button
                type="button"
                label="Cancel"
                icon="pi pi-times"
                onClick={handleCancel}
                outlined
                disabled={isUpdating}
              />
              <Button
                type="button"
                label="Save"
                icon="pi pi-check"
                severity="secondary"
                onClick={handleSave}
                disabled={!isAliasValid || isUpdating}
              />
            </ButtonGroup>
          </>
        ) : (
          <>
            <ConfirmationText>
              Are you sure you want to change the alias for device{" "}
              <strong>{serialNumber}</strong> from "{currentAlias}" to "
              {newAlias}"?
            </ConfirmationText>

            <ButtonGroup>
              <Button
                type="button"
                label="Cancel"
                icon="pi pi-times"
                onClick={handleCancel}
                outlined
                disabled={isUpdating}
              />
              <Button
                type="button"
                label={isUpdating ? "Updating..." : "Confirm"}
                icon={!isUpdating ? "pi pi-check" : "pi pi-spin pi-spinner"}
                severity="secondary"
                onClick={handleConfirmSave}
                disabled={isUpdating}
              />
            </ButtonGroup>
          </>
        )}
      </ModalContent>
    </Dialog>
  );
};
